import React, { useEffect, useMemo, useState } from 'react'
import { Layout } from '../../components/Layout/Layout'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { exportRowsAsCsv } from '../../utils/csv'
import { T, useContent } from '../../content/ContentProvider'

// Statuses defined in UIC 30301 §5.5 (booking lifecycle).
const STATUSES = ['BOOKED', 'CONFIRMED', 'CANCELED', 'REFUNDED', 'COMPLETED']

const statusPill = (s) => {
  const up = (s || '').toUpperCase()
  if (up === 'CONFIRMED' || up === 'COMPLETED') return 'ok'
  if (up === 'BOOKED') return 'neutral'
  if (up === 'CANCELED' || up === 'REFUNDED') return 'err'
  return 'neutral'
}

/**
 * Read-only audit view of ServiceStatusChange records — every booking-lifecycle
 * transition (BOOK → CONFIRM → CANCEL/REFUND/COMPLETE) submitted by carriers.
 */
export function ServiceStatus() {
  const { t } = useContent()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')

  useEffect(() => {
    setLoading(true)
    api
      .get('/api/servicestatuschanges')
      .then((r) => setRows(Array.isArray(r.data) ? r.data : r.data.content || []))
      .catch((err) => setError(err.response?.data?.message || t('serviceStatus.loadFailed')))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    let r = rows
    if (statusFilter !== 'all') {
      r = r.filter((x) => (x.newStatus || '').toUpperCase() === statusFilter)
    }
    if (search.trim()) {
      const s = search.toLowerCase()
      r = r.filter(
        (x) =>
          (x.transactionId || '').toLowerCase().includes(s) ||
          (x.agreementId || '').toLowerCase().includes(s) ||
          (x.sellerRics || '').toLowerCase().includes(s) ||
          (x.supplierRics || '').toLowerCase().includes(s) ||
          (x.sellerOrderId || '').toLowerCase().includes(s) ||
          (x.supplierOrderId || '').toLowerCase().includes(s)
      )
    }
    return r
  }, [rows, search, statusFilter])

  const counts = useMemo(() => {
    const c = { all: rows.length }
    STATUSES.forEach((s) => {
      c[s] = rows.filter((r) => (r.newStatus || '').toUpperCase() === s).length
    })
    return c
  }, [rows])

  return (
    <Layout crumb={t('serviceStatus.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h">{t('serviceStatus.title')}</h1>
              <div className="page-head__sub">
                <T k="serviceStatus.sub" />
              </div>
            </div>
            <div className="page-head__actions">
              <button
                className="btn"
                onClick={() =>
                  exportRowsAsCsv(
                    filtered,
                    [
                      { label: t('serviceStatus.csv.timestamp'), get: (x) => x.statusChangeTimeStamp || '' },
                      { key: 'transactionId', label: t('serviceStatus.csv.transaction') },
                      { key: 'agreementId', label: t('serviceStatus.csv.agreement') },
                      { key: 'newStatus', label: t('serviceStatus.csv.newStatus') },
                      { key: 'sellerName', label: t('serviceStatus.csv.seller') },
                      { key: 'sellerRics', label: t('serviceStatus.csv.sellerRics') },
                      { key: 'supplierName', label: t('serviceStatus.csv.supplier') },
                      { key: 'supplierRics', label: t('serviceStatus.csv.supplierRics') },
                      { key: 'sellerOrderId', label: t('serviceStatus.csv.sellerOrderId') },
                      { key: 'supplierOrderId', label: t('serviceStatus.csv.supplierOrderId') },
                    ],
                    'mas-service-status'
                  )
                }
              >
                {Icon.download({ s: 12 })} {t('serviceStatus.exportCsv')}
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        <div className="card">
          <div className="tab-bar">
            <button
              className={'tab-bar__tab ' + (statusFilter === 'all' ? 'is-active' : '')}
              onClick={() => setStatusFilter('all')}
            >
              {t('serviceStatus.tab.all')} <span className="count">{counts.all}</span>
            </button>
            {STATUSES.map((s) => (
              <button
                key={s}
                className={'tab-bar__tab ' + (statusFilter === s ? 'is-active' : '')}
                onClick={() => setStatusFilter(s)}
              >
                {s} <span className="count">{counts[s] || 0}</span>
              </button>
            ))}
          </div>

          <div className="filter-bar">
            <div className="search-input">
              {Icon.search({ s: 12 })}
              <input
                placeholder={t('serviceStatus.search.placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
              {t('serviceStatus.count', { shown: filtered.length, total: rows.length })}
            </span>
          </div>

          <div className="tbl-wrap">
            <table className="tbl table--cards">
              <thead>
                <tr>
                  <th>{t('serviceStatus.column.timestamp')}</th>
                  <th>{t('serviceStatus.column.transaction')}</th>
                  <th>{t('serviceStatus.column.newStatus')}</th>
                  <th>{t('serviceStatus.column.sellerSupplier')}</th>
                  <th>{t('serviceStatus.column.agreement')}</th>
                  <th>{t('serviceStatus.column.source')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="serviceStatus.loading" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="serviceStatus.empty" />
                    </td>
                  </tr>
                ) : (
                  filtered.map((x, i) => (
                    <tr key={x.id || i}>
                      <td className="muted mono" data-label={t('serviceStatus.column.timestamp')} style={{ fontSize: 11 }}>
                        {x.statusChangeTimeStamp ? new Date(x.statusChangeTimeStamp).toLocaleString('en-GB') : '—'}
                      </td>
                      <td className="id mono" data-label={t('serviceStatus.column.transaction')} style={{ fontSize: 11 }}>{x.transactionId || '—'}</td>
                      <td data-label={t('serviceStatus.column.newStatus')}>
                        <span className={'pill pill--' + statusPill(x.newStatus)}>
                          <span className="pill-dot" />
                          {x.newStatus || '—'}
                        </span>
                      </td>
                      <td className="muted" data-label={t('serviceStatus.column.sellerSupplier')}>
                        {x.sellerName || x.sellerRics || '—'}
                        <span style={{ margin: '0 6px', color: 'var(--text-dim)' }}>{t('serviceStatus.sellerSupplierSeparator')}</span>
                        {x.supplierName || x.supplierRics || '—'}
                      </td>
                      <td className="id" data-label={t('serviceStatus.column.agreement')}>{x.agreementId || '—'}</td>
                      <td className="muted" data-label={t('serviceStatus.column.source')}>
                        {x.statusChangeSourceName || x.statusChangeSourceRics || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
    </Layout>
  )
}
